import React, { useEffect, useRef, useState } from 'react'
import LayoutSidebar from '../components/LayoutSidebar'
import { setNotification } from '../redux/notificationSlice'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { useRequest } from '../hooks/UseRequest'
import Loading from '../components/Loading'
import { selectUser } from '../redux/userSlice'
import { selectAuth } from '../redux/authSlice'
import { selectDropdowns } from '../redux/dropdownSlice'
import { generateTableKey } from "../utils/listViewHelper";
import { handleToolTipMouseEnter } from "../helpers/layout";
import SuccessIcon from "../components/elements/SuccessIcon";
import DeclineIcon from "../components/elements/DeclineIcon";
import { setModal } from "../redux/modalSlice";
import { DateFormat } from '../helpers/formatting'
import { hasUserPermission } from "../utils/permissionHelper";

export default function Report({ type }) {
  const moduleReports = ['job_payment_batch_payments_report', 'job_payment_return_file_payments_report']
  const requesting = useRef(false);
  const dispatch = useDispatch();
  const request = useRequest();
  const user = useSelector(selectUser);
  const auth = useSelector(selectAuth)
  const [jobs, setJobs] = useState(null);
  const [generatedReport, setGeneratedReport] = useState([]);
  const dropdowns = useSelector(selectDropdowns);
  const active = useSelector(selectUser)
  const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken && dropdowns.data.length > 0) {
      getReportsAndUserDate();
    }
  }, [auth.accessToken, dropdowns.data])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken && dropdowns.data.length > 0) {
      const interval = setInterval(() => {
        getReportsStatus();
      }, 5000);
      return () => clearInterval(interval)
    }
  }, [auth.accessToken, dropdowns.data])

  const generateReportDDOData = (report) => {
    const dropdownData = [...dropdowns.data.find((x) => x.code_key === "job").dropdown_options];
    const returnData = { ...report, job_ddo: (dropdownData.filter((item) => item.optional_value_1 === 'TRUE')).find((j) => report.job_dropdown_option_id === j.id) }
    if (!returnData.job_ddo) {
      const ddoData = dropdownData.find((j) => report.job_dropdown_option_id === j.id);
      if (moduleReports.includes(ddoData.code_key)) {
        returnData.job_ddo = ddoData;
      }
    }
    return returnData;
  }

  const getReportsStatus = async () => {
    if (requesting.current) return;
    try {
      requesting.current = true;
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/report/status`;
      if (type === "tribal") {
        endpoint = `${process.env.REACT_APP_API_DOMAIN}/tribal_report/status`;
      }
      request.setEndpoint(endpoint);
      const response = await request.get()
      const generated_reports = response.data.data.map((report) => generateReportDDOData(report));
      setGeneratedReport(generated_reports);
      requesting.current = false;
    } catch (err) {
      dispatch(setNotification({ type: 'error', body: err.response?.data?.message }))
      requesting.current = false;
    }
  }

  const getReportsAndUserDate = async () => {
    if (requesting.current) return
    try {
      requesting.current = true
      let endpoint = `${process.env.REACT_APP_API_DOMAIN}/report`;
      if (type === "tribal") {
        endpoint = `${process.env.REACT_APP_API_DOMAIN}/tribal_report`;
      }
      request.setEndpoint(endpoint);
      const response = await request.get()
      const reports = dropdowns.data
        .find((x) => x.code_key === "job")
        .dropdown_options.filter((item) => {
          // Check for "tribal" type condition
          if (type === "tribal") {
            return item.optional_value_1 === 'TRUE' && item.optional_value_4 === 'TRIBAL_ONLY';
          }
          // Default filtering condition
          return item.optional_value_1 === 'TRUE';
        })
        .map((report) => {
          const report_subscription = response.data.report_subscriptions.find(
            (report_subscription) => report_subscription.id === report.id
          );
          return {
            ...report,
            is_subscribed: report_subscription
              ? report_subscription.is_subscribed
              : false,
          };
        });
      const generated_reports = response.data.generated_reports.map((report) => generateReportDDOData(report));
      setJobs(reports);
      setGeneratedReport(generated_reports);
      requesting.current = false;
    } catch (err) {
      dispatch(setNotification({ type: 'error', body: err.response?.data?.message }))
      requesting.current = false;
    }
  }

  const toggleSubscription = async (subscription_id, job_id) => {
    if (requesting.current) return;

    setJobs(() => jobs.map((report) => {
      if (report.id === job_id) {
        return { ...report, is_toggling: true }
      }
      return report;
    }))

    // @TODO: We should have enough data on the FE to avoid recalling the get endpoint?
    try {
      if (subscription_id) {
        requesting.current = true;
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/notification_subscription/${subscription_id}`)
        const response = await request.delete();
        if (response.status === 204) {
          requesting.current = false;
          getReportsAndUserDate();
        }
      } else {
        requesting.current = true;
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/notification_subscription`)
        const data = {
          notification_type_dropdown_option_id: dropdowns.data.find((item) => item.code_key === 'notification_type')?.dropdown_options.find((item) => item.code_key === 'notification_type_report').id,
          job_dropdown_option_id: job_id,
          user_id: user?.data?.id
        }
        const response = await request.post(data)
        if (response.status === 201) {
          requesting.current = false;
          getReportsAndUserDate();
        }
      }
    } catch (err) {
      dispatch(setNotification({ type: 'error', body: err.response?.data?.message }))
      requesting.current = false;
    }
  }

  const generateReport = async (job_id) => {
    if (requesting.current) return;

    setJobs(() => jobs.map((report) => {
      if (report.id === job_id) {
        return { ...report, is_creating: true }
      }
      return report;
    }))

    try {
      requesting.current = true;
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/report`)
      const response = await request.post({ job_dropdown_option_id: job_id });
      if (response.status === 201) {
        requesting.current = false;
        handleSuccessCreate(job_id, response.data);
      }
    } catch (err) {
      dispatch(setNotification({ type: 'error', body: err.response?.data?.message }))
      requesting.current = false;
    }
    setJobs(() => jobs.map((report) => {
      if (report.id === job_id) {
        return { ...report, is_creating: false }
      }
      return report;
    }))
  }

  const generateReportWithArgument = async (job_id) => {
    if (requesting.current) return;
    //If Job require date range
    const jobInfo = ([...dropdowns.data.find((x) => x.code_key === "job").dropdown_options].filter((item) => {
      // Check for "tribal" type condition
      if (type === "tribal") {
        return item.optional_value_1 === 'TRUE' && item.optional_value_4 === 'TRIBAL_ONLY';
      }
      // Default filtering condition
      return item.optional_value_1 === 'TRUE';
    })).find((j) => job_id === j.id);
    dispatch(
      setModal({
        is_active: true,
        type: 'report_date_range_modal',
        options: { jobInfo: jobInfo, successCallback: handleSuccessCreate }
      })
    )
  }

  const handleSuccessCreate = (jobId, jobData) => {
    removeTemplateReportFromCurrentReportList();
    //This is actual report object
    addGeneratedReportToCurrentReportList(getReportTemplate(jobId, jobData))
  }

  const getReportTemplate = (job_id, data) => {
    return {
      id: data.id,
      is_send_email: data.is_send_email,
      arguments: data.arguments,
      job_dropdown_option_id: job_id,
      status: data.status,
      job_ddo: jobs.find((job) => job.id === job_id),
    };
  }

  const removeTemplateReportFromCurrentReportList = () => {
    const new_report_list = generatedReport.filter((report) => report.status === "Generated");
    setGeneratedReport(new_report_list);
  }

  const addGeneratedReportToCurrentReportList = (report) => {
    const new_report_list = [report, ...generatedReport];
    setGeneratedReport(new_report_list);
  }

  const parseDate = (date) => {
    const dateParser = date.split('-');
    return dateParser[1] + '/' + dateParser[2] + '/' + dateParser[0];
  }

  const canCreateReport = (report) => {
    return (report?.code_key === 'job_export_call_recording' && hasUserPermission(activePermissions, 'can_export_call_recordings')) ||
      (report?.code_key === 'job_export_compliance_package' && hasUserPermission(activePermissions, 'can_export_compliance_package')) ||
      (report?.code_key === 'job_cash_out_refinance_report' && hasUserPermission(activePermissions, 'can_generate_cash_out_refinance_report')) ||
      (report?.code_key !== 'job_export_call_recording' && hasUserPermission(activePermissions, 'queue_report_file_create')) ||
      (report?.code_key !== 'job_export_call_recording' && hasUserPermission(activePermissions, 'queue_tribal_report_file_create'))
  }

  return (
    <LayoutSidebar
      id={false}
      contentTitle={"Reports"}
      content={(
        <>
          {jobs && (
            <div className="container m-0 p-2">
              <div className="columns is-flex-direction-column">
                <div className="lox-has-border-bottom">
                  <div className="column lox-hooktable is-9">
                    <table className="lox-hooktable-table is-9 table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
                      <thead>
                        <tr className="is-selected">
                          <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                            Report Name
                          </th>
                          <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                            One-Off Request
                            <span
                              className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}
                              data-tooltip={`Clicking the button will queue the report to be generated within 5 minutes. You will then be notified when the report has been generated.`}
                              onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}
                            >
                              <FontAwesomeIcon
                                icon={faQuestionCircle}
                                className={`pl-1 has-text-white`}
                              />
                            </span>
                          </th>
                          {/* TODO: Disable subscribe feature for a future fix */}
                          {/*<th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>*/}
                          {/*  Regular Subscription*/}
                          {/*  <span*/}
                          {/*    className={`lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-bottom has-tooltip-primary has-text-weight-normal`}*/}
                          {/*    data-tooltip={`As a subscriber you will receive this report regularly with a notification each time it is available, and a possible email notification.`}*/}
                          {/*    onMouseEnter={(toolTip) => handleToolTipMouseEnter(toolTip, 'div.lox-hooktable')}*/}
                          {/*  >*/}
                          {/*    <FontAwesomeIcon*/}
                          {/*      icon={faQuestionCircle}*/}
                          {/*      className={`pl-1 has-text-white`}*/}
                          {/*    />*/}
                          {/*  </span>*/}
                          {/*</th>*/}
                        </tr>
                      </thead>
                      <tbody>
                        {jobs?.map((report) => {
                          return canCreateReport(report) && (
                            <tr key={generateTableKey(report.id)}>
                              <td
                                className="has-text-centered"
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                {report.name}
                              </td>
                              <td
                                className="has-text-centered"
                                style={{ whiteSpace: 'nowrap' }}
                              >
                                {!report?.optional_value_2 && (
                                  <button
                                    type="button"
                                    className="button is-small has-text-weight-bold has-background-grey has-text-white pl-3 pr-3 mr-3"
                                    onClick={() => generateReport(report.id)}
                                  >{report.is_creating ? <span> Creating <Loading size={'fa-xs'} noWrapper /> </span> : `Generate`}</button>
                                )}
                                {(report?.optional_value_2 || report?.optional_value_3) && (
                                  <button
                                    type="button"
                                    className="button is-small has-text-weight-bold has-background-grey has-text-white pl-3 pr-3"
                                    onClick={() => generateReportWithArgument(report.id)}
                                  >{`Generate With Arguments`}</button>
                                )}
                              </td>
                              {/* TODO: Disable subscribe feature for a future fix */}
                              {/*<td*/}
                              {/*  className="has-text-centered"*/}
                              {/*  style={{ whiteSpace: 'nowrap' }}*/}
                              {/*>*/}
                              {/*  <button*/}
                              {/*    type="button"*/}
                              {/*    className={report.is_subscribed ? 'button is-small has-text-weight-bold has-background-error has-background-danger has-text-white pl-3 pr-3' : 'button is-small has-text-weight-bold has-background-primary has-text-white pl-3 pr-3'}*/}
                              {/*    onClick={() => toggleSubscription(report.is_subscribed, report.id)}*/}
                              {/*  >{report.is_subscribed ? 'Un-Subscribe' : 'Subscribe'} {report.is_toggling && <span> <Loading size={'fa-xs'} noWrapper /> </span>}</button>*/}
                              {/*</td>*/}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="column lox-hooktable is-12">
                  <p className="has-text-weight-bold is-size-6"> Generated Reports </p>
                  <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-hoverable is-fullwidth">
                    <thead>
                      <tr className="is-selected">
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          Download
                        </th>
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          ID #
                        </th>
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          Report
                        </th>
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          Date Requested
                        </th>
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          Status
                        </th>
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          Arguments
                        </th>
                        <th className="is-selected has-text-centered" style={{ whiteSpace: 'nowrap' }}>
                          Email
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedReport.map((generated_report, index) => (
                        <tr>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.uri && (
                              <a href={generated_report.uri} target="_blank" rel="noreferrer">
                                <FontAwesomeIcon
                                  icon={faDownload}
                                  className={`pl-1 has-text-primary`}
                                />
                              </a>
                            )}
                            {(generated_report?.status === 'Processing' || generated_report?.status === 'Pending') && (
                              <Loading size={'fa-xs'} noWrapper />
                            )}
                          </td>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.id}
                          </td>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.job_ddo?.name}
                          </td>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.created_at ? <DateFormat dateString={generated_report.created_at} formatString="MM/dd/yyyy hh:mm:ss a" /> : <DateFormat dateString={new Date().toISOString()} formatString="MM/dd/yyyy hh:mm:ss a" />}
                          </td>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.status === "template" ? <span> Processing </span> : generated_report?.status}
                          </td>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.arguments && (
                              Object.entries(generated_report?.arguments).map(([key, val], index) => (
                                (key === 'start-date' || key === 'end-date') ? (
                                  <span> {index > 0 && (` | `)}{parseDate(val)} </span>
                                ) : <span> {index > 0 && (` | `)}{val} </span>))
                            )}
                          </td>
                          <td
                            className="has-text-centered"
                            style={{ whiteSpace: 'nowrap' }}
                          >
                            {generated_report?.is_send_email ? SuccessIcon() : DeclineIcon()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {generatedReport.length === 0 && (
                    <p className="is-size-12 is-italic has-text-centered has-text-grey">No Record(s) Found</p>
                  )}
                </div>
              </div>
            </div>
          )}
          {!jobs && (
            <Loading />
          )}
        </>
      )}
      dashlets={false}
    />
  )
}
